import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicHotelService } from '../../services/api';
import { publicApi } from '../../services/api';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ReservationModal from '../reservations/components/ReservationModal';

const HotelLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationFormData, setReservationFormData] = useState(null);
  
  // Visualização: 'card' | 'list'
  const [viewMode, setViewMode] = useState('card');
  // Ordenação: 'default' = conforme API, 'preco' | 'capacidade' | 'disponibilidade'
  const [sortOrder, setSortOrder] = useState('default');
  // Cliente já clicou em Buscar Quartos (LP só mostra disponibilidade após clicar)
  const [hasSearched, setHasSearched] = useState(false);

  // Calcular datas padrão usando useMemo (hoje e amanhã)
  const { defaultCheckIn, defaultCheckOut, today, tomorrow } = useMemo(() => {
    const todayDate = new Date();
    const tomorrowDate = new Date(todayDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    return {
      defaultCheckIn: todayDate.toISOString().split('T')[0],
      defaultCheckOut: tomorrowDate.toISOString().split('T')[0],
      today: todayDate,
      tomorrow: tomorrowDate
    };
  }, []);

  // Filtros de busca (datas padrão: hoje e amanhã)
  const [searchFilters, setSearchFilters] = useState({
    checkInDate: defaultCheckIn,
    checkOutDate: defaultCheckOut,
    adults: 1,
    children: 0
  });

  const fetchHotel = useCallback(async (options = {}) => {
    const { checkIn = null, checkOut = null, withAvailability = false } = options;
    try {
      setLoading(true);
      const normalizedCode = code.toLowerCase().trim();
      // Só envia datas quando withAvailability (clique em Buscar Quartos)
      const checkInDate = withAvailability ? (checkIn || searchFilters.checkInDate || defaultCheckIn) : null;
      const checkOutDate = withAvailability ? (checkOut || searchFilters.checkOutDate || defaultCheckOut) : null;
      const data = await publicHotelService.getByCode(normalizedCode, checkInDate, checkOutDate);
      setHotel(data);
    } catch (err) {
      console.error('❌ Erro ao carregar hotel:', err);
      console.error('❌ Detalhes do erro:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Hotel não encontrado');
    } finally {
      setLoading(false);
    }
  }, [code, searchFilters.checkInDate, searchFilters.checkOutDate, defaultCheckIn, defaultCheckOut]);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setHasSearched(true);
    const load = async () => {
      try {
        setLoading(true);
        const normalizedCode = code.toLowerCase().trim();
        const data = await publicHotelService.getByCode(
          normalizedCode,
          defaultCheckIn,
          defaultCheckOut
        );
        if (!cancelled) setHotel(data);
      } catch (err) {
        if (!cancelled) {
          console.error('❌ Erro ao carregar hotel:', err);
          setError(err.response?.data?.message || 'Hotel não encontrado');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [code, defaultCheckIn, defaultCheckOut]);

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    const checkIn = searchFilters.checkInDate || defaultCheckIn;
    const checkOut = searchFilters.checkOutDate || defaultCheckOut;
    const adults = searchFilters.adults ?? 1;
    const children = searchFilters.children ?? 0;
    const roomType = room.type?.name || room.type?.label || (typeof room.type === 'string' ? room.type : 'Quarto');
    setReservationFormData({
      roomId: room.id,
      roomNumber: room.number,
      roomType,
      checkInDate: checkIn,
      checkOutDate: checkOut,
      checkInTime: '14:00',
      checkOutTime: '12:00',
      adults,
      children,
      hotelId: hotel?.id ?? null,
      guestName: '',
      guestEmail: '',
      guestPhone: '',
      originCity: '',
      documentNumber: '',
      specialRequests: ''
    });
    // Refetch hotel para trazer minChildChargeAge e childDiscountPercent; depois abre o modal
    fetchHotel({ checkIn, checkOut, withAvailability: true }).then(() => setShowReservationModal(true)).catch(() => setShowReservationModal(true));
  };

  const handleSaveReservationFromModal = async (reservationData) => {
    const payload = {
      roomId: reservationData.roomId,
      checkInDate: reservationData.checkInDate,
      checkOutDate: reservationData.checkOutDate,
      adults: Number(reservationData.adults) || 1,
      children: Number(reservationData.children) || 0,
      guestName: reservationData.guestName,
      guestEmail: reservationData.guestEmail || null,
      guestPhone: reservationData.guestPhone ? String(reservationData.guestPhone).replace(/\D/g, '') : null,
      specialRequests: reservationData.specialRequests || null,
      bookingSource: 'LANDING_PAGE'
    };
    const response = await publicApi.post('/reservations/public', payload);
    if (response.data?.completionToken) {
      navigate(`/reservation-completion/${response.data.completionToken}`);
    }
    return response.data;
  };

  const formatPrice = (price) => {
    if (!price) return 'Consulte';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(price);
  };

  const translateRoomType = (type) => {
    if (typeof type === 'object' && type !== null) {
      return type.label || type.name || 'Quarto';
    }
    const types = {
      'SINGLE': 'Solteiro',
      'DOUBLE': 'Casal',
      'TWIN': 'Twin',
      'TRIPLE': 'Triplo',
      'SUITE': 'Suíte',
      'FAMILY': 'Família'
    };
    return types[type] || type || 'Quarto';
  };

  const calculateNights = () => {
    if (!searchFilters.checkInDate || !searchFilters.checkOutDate) return 0;
    const checkIn = new Date(searchFilters.checkInDate);
    const checkOut = new Date(searchFilters.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const nights = calculateNights();

  /** Quartos ordenados conforme filtro selecionado; default = ordem da API */
  const sortedRooms = useMemo(() => {
    const rooms = hotel?.rooms ?? [];
    if (rooms.length === 0) return [];
    if (sortOrder === 'default') return [...rooms];
    const copy = [...rooms];
    switch (sortOrder) {
      case 'preco':
        return copy.sort((a, b) => (Number(a.singlePrice ?? 0) - Number(b.singlePrice ?? 0)));
      case 'capacidade':
        return copy.sort((a, b) => (Number(b.capacity ?? 0) - Number(a.capacity ?? 0)));
      case 'disponibilidade':
        return copy.sort((a, b) => {
          const avA = !a.status || a.status === 'AVAILABLE' ? 1 : 0;
          const avB = !b.status || b.status === 'AVAILABLE' ? 1 : 0;
          return avB - avA; // disponíveis primeiro
        });
      default:
        return copy;
    }
  }, [hotel?.rooms, sortOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-slate-600">Carregando informações do hotel...</p>
        </div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Hotel não encontrado</h1>
          <p className="text-muted-foreground mb-6">{error || 'O hotel solicitado não foi encontrado.'}</p>
          <Button onClick={() => navigate('/')}>Voltar</Button>
        </div>
      </div>
    );
  }

  const todayStr = today.toISOString().split('T')[0];
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section com Busca Integrada */}
      <section className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent pt-8 pb-20 md:pt-12 md:pb-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -ml-48 -mb-48"></div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Hotel Info */}
          <div className="max-w-4xl mx-auto text-center mb-8">
            {hotel.logo && (
              <div className="mb-4 flex justify-center">
                <img 
                  src={hotel.logo} 
                  alt={hotel.name}
                  className="max-h-16 md:max-h-20 object-contain drop-shadow-lg"
                  style={{ maxHeight: hotel.logoSize ? `${Math.min(hotel.logoSize, 80)}px` : '80px' }}
                />
              </div>
            )}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3">
              {hotel.name}
            </h1>
            {hotel.address && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground mb-3">
                <Icon name="MapPin" size={18} />
                <p className="text-sm md:text-base">{hotel.address}, {hotel.city} - {hotel.state}</p>
              </div>
            )}
            {(hotel.phone || hotel.email) && (
              <div className="flex flex-wrap items-center justify-center gap-4 text-muted-foreground text-sm">
                {hotel.phone && (
                  <a href={`tel:${hotel.phone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Icon name="Phone" size={16} />
                    <span>{hotel.phone}</span>
                  </a>
                )}
                {hotel.email && (
                  <a href={`mailto:${hotel.email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Icon name="Mail" size={16} />
                    <span>{hotel.email}</span>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Busca Integrada */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white dark:bg-card rounded-2xl shadow-2xl border border-border p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Check-in */}
                <div className="md:border-r border-border md:pr-4">
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Check-in
                  </label>
                  <input
                    type="date"
                    value={searchFilters.checkInDate || defaultCheckIn}
                    onChange={(e) => handleFilterChange('checkInDate', e.target.value)}
                    onClick={(e) => e.target.showPicker?.()}
                    min={todayStr}
                    className="w-full min-h-[2.5rem] text-base font-medium text-foreground bg-transparent border-0 p-0 focus:outline-none focus:ring-0 cursor-pointer [color-scheme:inherit] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100"
                  />
                </div>

                {/* Check-out */}
                <div className="md:border-r border-border md:pr-4">
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Check-out
                  </label>
                  <input
                    type="date"
                    value={searchFilters.checkOutDate || defaultCheckOut}
                    onChange={(e) => handleFilterChange('checkOutDate', e.target.value)}
                    onClick={(e) => e.target.showPicker?.()}
                    min={searchFilters.checkInDate || defaultCheckIn}
                    className="w-full min-h-[2.5rem] text-base font-medium text-foreground bg-transparent border-0 p-0 focus:outline-none focus:ring-0 cursor-pointer [color-scheme:inherit] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100"
                  />
                  {nights > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{nights} {nights === 1 ? 'noite' : 'noites'}</p>
                  )}
                </div>

                {/* Hóspedes */}
                <div className="md:border-r border-border md:pr-4">
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Hóspedes
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon name="Users" size={16} className="text-muted-foreground" />
                        <span className="text-base font-medium text-foreground">
                          {searchFilters.adults + searchFilters.children} {searchFilters.adults + searchFilters.children === 1 ? 'pessoa' : 'pessoas'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <button
                          onClick={() => handleFilterChange('adults', Math.max(1, searchFilters.adults - 1))}
                          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <Icon name="MinusCircle" size={12} />
                        </button>
                        <span>{searchFilters.adults} adulto{searchFilters.adults !== 1 ? 's' : ''}</span>
                        <button
                          onClick={() => handleFilterChange('adults', searchFilters.adults + 1)}
                          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <Icon name="PlusCircle" size={12} />
                        </button>
                      </div>
                      {searchFilters.children > 0 && (
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <button
                            onClick={() => handleFilterChange('children', Math.max(0, searchFilters.children - 1))}
                            className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                          >
                            <Icon name="MinusCircle" size={12} />
                          </button>
                          <span>{searchFilters.children} criança{searchFilters.children !== 1 ? 's' : ''}</span>
                          <button
                            onClick={() => handleFilterChange('children', searchFilters.children + 1)}
                            className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                          >
                            <Icon name="PlusCircle" size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Botão Buscar */}
                <div className="flex items-end">
                  <Button
                    variant="default"
                    className="w-full md:w-auto px-6 py-3 h-auto text-base font-semibold"
                    onClick={() => {
                      setHasSearched(true);
                      fetchHotel({ withAvailability: true });
                      setTimeout(() => {
                        const roomsSection = document.getElementById('rooms-section');
                        roomsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  >
                    <Icon name="Search" size={18} className="mr-2" />
                    Buscar Quartos
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Section */}
      <section id="rooms-section" className="py-12 md:py-20 -mt-10 md:-mt-16 relative z-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3">
              Nossos Quartos
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Escolha o quarto perfeito para sua estadia
            </p>
          </div>

          {!hasSearched ? (
            <div className="text-center py-16 max-w-md mx-auto">
              <Icon name="Search" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground">
                Selecione as datas de check-in e check-out e clique em <strong>Buscar Quartos</strong> para ver a disponibilidade.
              </p>
            </div>
          ) : hotel.rooms && hotel.rooms.length > 0 ? (
            <>
              {/* Controles: visualização e ordenação */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Visualizar:</span>
                  <div className="flex items-center bg-muted/30 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        viewMode === 'card'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      title="Visualização em Cards"
                    >
                      <Icon name="LayoutGrid" size={16} />
                      <span>Card</span>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        viewMode === 'list'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      title="Visualização em Lista"
                    >
                      <Icon name="List" size={16} />
                      <span>Lista</span>
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="sort-rooms" className="text-sm font-medium text-muted-foreground">
                    Ordenar por:
                  </label>
                  <select
                    id="sort-rooms"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="default">Padrão</option>
                    <option value="preco">Preço</option>
                    <option value="capacidade">Capacidade</option>
                    <option value="disponibilidade">Disponibilidade</option>
                  </select>
                </div>
              </div>

              {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-7xl mx-auto">
              {sortedRooms.map((room) => {
                const isAvailable = !room.status || room.status === 'AVAILABLE';
                const singlePrice = room.singlePrice ? Number(room.singlePrice) : 0;
                const totalPrice = singlePrice * (searchFilters.adults + searchFilters.children) * (nights || 1);
                
                return (
                  <div
                    key={room.id}
                    className={`group bg-white dark:bg-card border border-border rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${
                      isAvailable 
                        ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer' 
                        : 'opacity-75 cursor-not-allowed'
                    }`}
                  >
                    {/* Room Image Placeholder */}
                    <div className="relative h-32 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Icon name="Hotel" size={40} className="text-primary/20" />
                      </div>
                      {!isAvailable && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="bg-white/90 dark:bg-card/90 px-3 py-1.5 rounded-lg">
                            <span className="text-xs font-semibold text-foreground">
                              {room.status === 'OCCUPIED' ? 'Ocupado' : room.status === 'RESERVED' ? 'Reservado' : 'Indisponível'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-foreground mb-0.5">
                            Quarto {room.number}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {translateRoomType(room.type)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                          <Icon name="Users" size={12} />
                          <span className="text-xs font-semibold">{room.capacity}</span>
                        </div>
                      </div>

                      {/* Description */}
                      {room.description && (
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                          {room.description}
                        </p>
                      )}

                      {/* Configuração de Camas */}
                      {(room.bedsSolteiro > 0 || room.bedsCasal > 0 || room.bedsQueen > 0 || room.bedsKing > 0 || room.bedsBeliche > 0) && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {room.bedsSolteiro > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                              <Icon name="Bed" size={12} />
                              <span>Solteiro: {room.bedsSolteiro}</span>
                            </div>
                          )}
                          {room.bedsCasal > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                              <Icon name="Bed" size={12} />
                              <span>Casal: {room.bedsCasal}</span>
                            </div>
                          )}
                          {room.bedsQueen > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                              <Icon name="Bed" size={12} />
                              <span>Queen: {room.bedsQueen}</span>
                            </div>
                          )}
                          {room.bedsKing > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                              <Icon name="Bed" size={12} />
                              <span>King: {room.bedsKing}</span>
                            </div>
                          )}
                          {room.bedsBeliche > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                              <Icon name="Bed" size={12} />
                              <span>Beliche: {room.bedsBeliche}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Amenities */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {room.hasBathroom && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                            <Icon name="Shower" size={12} />
                            <span>Banheiro</span>
                          </div>
                        )}
                        {room.hasAC && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                            <Icon name="Snowflake" size={12} />
                            <span>Ar</span>
                          </div>
                        )}
                        {room.hasMinibar && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                            <Icon name="Package" size={12} />
                            <span>Frigobar</span>
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div className="border-t border-border pt-3 mb-3">
                        <div className="flex items-baseline justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">A partir de</p>
                            <p className="text-xl font-bold text-primary">
                              {formatPrice(room.singlePrice)}
                            </p>
                            <p className="text-xs text-muted-foreground">por pessoa</p>
                          </div>
                        </div>
                        {nights > 0 && searchFilters.checkInDate && searchFilters.checkOutDate && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {nights} {nights === 1 ? 'noite' : 'noites'} × {searchFilters.adults + searchFilters.children} {searchFilters.adults + searchFilters.children === 1 ? 'pessoa' : 'pessoas'}
                              </span>
                              <span className="font-semibold text-foreground">
                                {formatPrice(totalPrice)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Status Badge */}
                      {room.status && room.status !== 'AVAILABLE' && (
                        <div className="mb-3">
                          <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${
                            room.status === 'OCCUPIED' 
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                              : room.status === 'RESERVED'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            <Icon 
                              name={room.status === 'OCCUPIED' ? 'UserX' : 'Clock'} 
                              size={12} 
                            />
                            <span>
                              {room.status === 'OCCUPIED' 
                                ? 'Ocupado' 
                                : room.status === 'RESERVED'
                                ? 'Reservado'
                                : room.status === 'MAINTENANCE'
                                ? 'Em Manutenção'
                                : room.status === 'CLEANING'
                                ? 'Em Limpeza'
                                : room.status}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Button */}
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full text-sm font-semibold"
                        onClick={() => handleRoomSelect(room)}
                        disabled={!isAvailable}
                      >
                        <Icon name="Calendar" size={16} className="mr-2" />
                        {isAvailable ? 'Reservar Agora' : 'Indisponível'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
              ) : (
            <div className="bg-white dark:bg-card border border-border rounded-xl overflow-hidden max-w-7xl mx-auto">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Quarto</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Capacidade</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Preço</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedRooms.map((room) => {
                      const isAvailable = !room.status || room.status === 'AVAILABLE';
                      const singlePrice = room.singlePrice ? Number(room.singlePrice) : 0;
                      const totalPrice = singlePrice * (searchFilters.adults + searchFilters.children) * (nights || 1);
                      return (
                        <tr
                          key={room.id}
                          className={`hover:bg-muted/30 transition-colors ${!isAvailable ? 'opacity-75' : 'cursor-pointer'}`}
                          onClick={() => isAvailable && handleRoomSelect(room)}
                        >
                          <td className="px-4 py-3">
                            <span className="font-semibold text-foreground">Quarto {room.number}</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{translateRoomType(room.type)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-primary bg-primary/10 px-2 py-0.5 rounded-md text-xs font-semibold">
                              <Icon name="Users" size={12} />
                              {room.capacity}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-bold text-primary">{formatPrice(room.singlePrice)}</p>
                            {nights > 0 && (
                              <p className="text-xs text-muted-foreground">{formatPrice(totalPrice)} total</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${
                              isAvailable
                                ? 'bg-success/10 text-success'
                                : room.status === 'OCCUPIED'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                : room.status === 'RESERVED'
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {isAvailable ? 'Disponível' : room.status === 'OCCUPIED' ? 'Ocupado' : room.status === 'RESERVED' ? 'Reservado' : room.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleRoomSelect(room); }}
                              disabled={!isAvailable}
                            >
                              <Icon name="Calendar" size={14} className="mr-1" />
                              {isAvailable ? 'Reservar' : 'Indisponível'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <Icon name="Hotel" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground">Nenhum quarto disponível no momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal de reserva (mesmo do Dashboard) */}
      {hotel && (
        <ReservationModal
          isOpen={showReservationModal}
          onClose={() => {
            setShowReservationModal(false);
            setReservationFormData(null);
            setSelectedRoom(null);
          }}
          reservation={reservationFormData}
          onSave={handleSaveReservationFromModal}
          mode="create"
          source="landing"
          hotel={hotel}
        />
      )}

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">{hotel.name}</h3>
                {hotel.address && (
                  <p className="text-sm text-muted-foreground">{hotel.address}</p>
                )}
                <p className="text-sm text-muted-foreground">{hotel.city} - {hotel.state}</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Contato</h3>
                {hotel.phone && (
                  <a href={`tel:${hotel.phone}`} className="text-sm text-muted-foreground hover:text-primary block mb-1">
                    {hotel.phone}
                  </a>
                )}
                {hotel.email && (
                  <a href={`mailto:${hotel.email}`} className="text-sm text-muted-foreground hover:text-primary block">
                    {hotel.email}
                  </a>
                )}
              </div>
              {hotel.website && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Website</h3>
                  <a href={hotel.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                    Visitar site
                  </a>
                </div>
              )}
            </div>
            <div className="border-t border-border pt-6 text-center">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} {hotel.name}. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HotelLanding;
